import { useState } from "react";
import { Search } from "lucide-react";
import { Plan, PlanStatus } from "@snipe/core";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardNote, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/empty-state";
import { Field, Input, Select } from "@/components/ui/field";
import { Loading, LoadFailure } from "@/components/query-state";
import { StatusChip } from "@/components/status";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import {
  USER_SEARCH_MIN,
  useDeleteUser,
  useRevokeSessions,
  useSetUserPlan,
  useUserDetail,
  useUserSearch,
} from "@/lib/queries";
import { runFailed } from "@/lib/schemas";
import { formatDuration, formatRelative, formatStamp } from "@/lib/time";
import { useNow } from "@/lib/use-now";

/**
 * Support: one customer at a time.
 *
 * The page is built around the email in the inbox, which is the only handle
 * support ever has. Everything else on screen is here because it answers a
 * question somebody actually asks: "am I getting alerts" (the alerts and
 * their runs), "I paid and nothing changed" (the plan), "I lost my phone"
 * (revoke), "delete my account" (delete).
 *
 * Nothing here can show a password hash, a session token or anything from an
 * identity's stored Facebook login: the API does not return any of them, and
 * the schemas in lib/schemas.ts would strip them if it did.
 */

/** How many of the customer's runs to show. The API sends at most fifty. */
const RUNS_SHOWN = 12;

export function UsersPage() {
  const now = useNow();
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const found = useUserSearch(query);
  const detail = useUserDetail(selected);
  const setPlan = useSetUserPlan();
  const revoke = useRevokeSessions();
  const remove = useDeleteUser();

  const user = detail.data?.user ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-base font-semibold">Users</h1>
      </div>

      <Card>
        <CardBody>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setResult(null);
              setQuery(draft.trim());
            }}
          >
            <Field
              label="Email"
              htmlFor="user-search"
              className="min-w-[22rem] flex-1"
              hint="Part of an address is enough."
            >
              <Input
                id="user-search"
                value={draft}
                placeholder="jo@"
                onChange={(event) => setDraft(event.target.value)}
              />
            </Field>
            <Button type="submit" size="md" disabled={draft.trim().length < USER_SEARCH_MIN}>
              <Search aria-hidden="true" className="h-3.5 w-3.5" />
              Search
            </Button>
          </form>
        </CardBody>
      </Card>

      {result !== null ? (
        <p role="status" className="rounded-md border border-border bg-muted px-3 py-2 text-sm">
          {result}
        </p>
      ) : null}

      {query !== "" ? (
        <Card>
          <CardHeader>
            <CardTitle>{found.data?.length ?? 0} matching</CardTitle>
            <CardNote>{query}</CardNote>
          </CardHeader>

          {found.isPending ? <Loading what="the search" /> : null}
          {found.error !== null ? (
            <LoadFailure what="the search" error={found.error} onRetry={() => void found.refetch()} />
          ) : null}

          {found.data !== undefined && found.data.length === 0 ? (
            <EmptyState title="No account with that address.">
              Try a shorter part of it.
            </EmptyState>
          ) : null}

          {found.data !== undefined && found.data.length > 0 ? (
            <CardBody className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Email</TH>
                    <TH>Role</TH>
                    <TH>Plan</TH>
                    <TH>Joined</TH>
                  </TR>
                </THead>
                <TBody>
                  {found.data.map((row) => (
                    <TR key={row.id}>
                      <TD className="font-medium">
                        <button
                          type="button"
                          className="underline underline-offset-4"
                          onClick={() => {
                            setResult(null);
                            setSelected(row.id);
                          }}
                        >
                          {row.email}
                        </button>
                      </TD>
                      <TD>
                        <StatusChip tone={row.role === "admin" ? "warn" : "muted"}>
                          {row.role}
                        </StatusChip>
                      </TD>
                      <TD>
                        <StatusChip tone={row.planStatus === "active" ? "ok" : "muted"}>
                          {row.plan} · {row.planStatus}
                        </StatusChip>
                      </TD>
                      <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                        {formatStamp(row.createdAt)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </CardBody>
          ) : null}
        </Card>
      ) : null}

      {detail.isPending && selected !== null ? <Loading what="the account" /> : null}
      {detail.error !== null ? (
        <LoadFailure
          what="the account"
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      {user !== null && detail.data !== undefined ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{user.email}</CardTitle>
              <CardNote>
                <button
                  type="button"
                  className="underline underline-offset-4"
                  onClick={() => setSelected(null)}
                >
                  Close
                </button>
              </CardNote>
            </CardHeader>
            <CardBody className="flex flex-wrap items-end gap-4">
              <Field label="Plan" htmlFor="user-plan" className="w-40">
                <Select
                  id="user-plan"
                  value={user.plan}
                  onChange={(event) => {
                    setResult(null);
                    setPlan.mutate(
                      {
                        id: user.id,
                        plan: event.target.value as Plan,
                        planStatus: user.planStatus,
                      },
                      {
                        onSuccess: (updated) => setResult(`Plan is now ${updated.plan}.`),
                        onError: (error) => setResult(`Could not change the plan: ${error.message}`),
                      },
                    );
                  }}
                >
                  {Plan.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Status" htmlFor="user-plan-status" className="w-52">
                <Select
                  id="user-plan-status"
                  value={user.planStatus}
                  onChange={(event) => {
                    setResult(null);
                    setPlan.mutate(
                      {
                        id: user.id,
                        plan: user.plan,
                        planStatus: event.target.value as PlanStatus,
                      },
                      {
                        onSuccess: (updated) => setResult(`Status is now ${updated.planStatus}.`),
                        onError: (error) =>
                          setResult(`Could not change the status: ${error.message}`),
                      },
                    );
                  }}
                >
                  {PlanStatus.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className="ml-auto flex items-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => {
                    setResult(null);
                    revoke.mutate(user.id, {
                      onSuccess: ({ revoked }) =>
                        setResult(`Signed out of ${revoked} session(s).`),
                      onError: (error) => setResult(`Could not sign them out: ${error.message}`),
                    });
                  }}
                >
                  Sign out everywhere
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setResult(null);
                    setConfirmDelete(true);
                  }}
                >
                  Delete account
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{detail.data.alerts.length} alerts</CardTitle>
            </CardHeader>
            {detail.data.alerts.length === 0 ? (
              <EmptyState title="No alerts on this account.">
                Nothing will arrive until they make one.
              </EmptyState>
            ) : (
              <CardBody className="p-0">
                <Table>
                  <THead>
                    <TR>
                      <TH>Name</TH>
                      <TH>Query</TH>
                      <TH className="text-right">Every</TH>
                      <TH>State</TH>
                      <TH>Last run</TH>
                      <TH>Next due</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {detail.data.alerts.map((alert) => (
                      <TR key={alert.id}>
                        <TD className="font-medium">{alert.name}</TD>
                        <TD className="text-muted-foreground">{alert.filters.query}</TD>
                        <TD data-numeric className="text-right">
                          {formatDuration(alert.intervalSeconds * 1000)}
                        </TD>
                        <TD>
                          <StatusChip
                            tone={alert.active ? "ok" : "muted"}
                            icon={alert.active ? "default" : "pause"}
                          >
                            {alert.active ? "active" : "paused"}
                          </StatusChip>
                        </TD>
                        <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                          {alert.lastRunAt === null ? "never" : formatRelative(alert.lastRunAt, now)}
                        </TD>
                        <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                          {alert.nextDueAt === null ? "-" : formatStamp(alert.nextDueAt)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </CardBody>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent runs</CardTitle>
              <CardNote>newest {RUNS_SHOWN}</CardNote>
            </CardHeader>
            {detail.data.runs.length === 0 ? (
              <EmptyState title="Nothing has run for this account.">
                Check the overview for a worker.
              </EmptyState>
            ) : (
              <CardBody className="p-0">
                <Table>
                  <THead>
                    <TR>
                      <TH>When</TH>
                      <TH>Alert</TH>
                      <TH>Outcome</TH>
                      <TH className="text-right">Listings seen</TH>
                      <TH className="text-right">New matches</TH>
                      <TH>Error</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {detail.data.runs.slice(0, RUNS_SHOWN).map((run) => (
                      <TR key={run.id}>
                        <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                          {formatRelative(run.ranAt, now)}
                        </TD>
                        <TD className="font-mono text-xs" title={run.alertId}>
                          {run.alertId.slice(0, 8)}
                        </TD>
                        <TD>
                          <StatusChip tone={runFailed(run) ? "danger" : "ok"}>
                            {run.outcome}
                          </StatusChip>
                        </TD>
                        <TD data-numeric className="text-right">
                          {run.listingsSeen}
                        </TD>
                        <TD data-numeric className="text-right">
                          {run.newMatches}
                        </TD>
                        <TD className="text-muted-foreground">{run.errorKind ?? "-"}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </CardBody>
            )}
          </Card>
        </>
      ) : null}

      {/*
        A dialog, not two taps, and the only one on this page: deleting an
        account takes their alerts and every match under them with it, and
        there is no undo and no soft-delete column to un-set. The address is
        repeated in the dialog because support is usually looking at two
        accounts by the time they get here.
      */}
      <Dialog open={confirmDelete} onOpenChange={(open) => (open ? undefined : setConfirmDelete(false))}>
        <DialogContent role="alertdialog">
          <DialogTitle>Delete {user?.email ?? "this account"}?</DialogTitle>
          <DialogDescription>
            Their alerts and matches go too. This cannot be undone.
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (user === null) return;
                const email = user.email;
                remove.mutate(user.id, {
                  onSuccess: () => {
                    setResult(`Deleted ${email}.`);
                    setSelected(null);
                  },
                  onError: (error) => setResult(`Could not delete ${email}: ${error.message}`),
                  onSettled: () => setConfirmDelete(false),
                });
              }}
            >
              Delete account
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
